import React, { useState, useEffect } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Globe, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { OrangeScoreReveal, CategoriesGrid } from "@/optimise/AnimatedScanReveal";
import BulkProspectScanPanel from "@/hq/clients/BulkProspectScanPanel";
import { normalizeWebsiteInput } from "@/lib/url";
import { PhoneCountryFields } from "@/optimise/PhoneCountryFields";
import { inferCountryFromAddress, inferCountryFromPhone } from "@/lib/countryInference";

const STAGES = [
  "SCANNING WEBSITE...",
  "ANALYSING HEALTH...",
  "FINDING BUSINESS DETAILS...",
  "CHECKING ORANGE CORE...",
  "READY TO REVIEW",
];

function StagePulse({ stage, done }) {
  return (
    <div className="flex items-center gap-3 py-2 text-sm">
      {done ? <CheckCircle2 className="text-[#0FA958]" size={16}/> : <Loader2 className="text-[#FF6A00] animate-spin" size={16}/>}
      <span className={done ? "text-[#4A4A4A]" : "font-black tracking-[0.08em]"}>{stage}</span>
    </div>
  );
}

export default function ScanWebsiteModal({ open, onClose, onDone, onRefetch }) {
  const [mode, setMode] = useState("single");   // 'single' | 'bulk'
  const [step, setStep] = useState("url");    // url | scanning | review | error
  const [url, setUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [chosenEmail, setChosenEmail] = useState("");
  const [chosenPhone, setChosenPhone] = useState("");
  const [dupChoice, setDupChoice] = useState("new");   // 'new' | business_id
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!open) return null;

  const reset = () => {
    setStep("url"); setUrl(""); setProgress(0);
    setResult(null); setForm(null); setChosenEmail("");
    setChosenPhone(""); setDupChoice("new");
  };

  const closeAll = () => { reset(); onClose(); };

  const runScan = async (e) => {
    e && e.preventDefault();
    // Shared canonical normaliser — accepts `example.com`, `www.example.com`
    // and any variant with/without scheme. Never require the raw field
    // text to contain http(s)://.
    const canonicalUrl = normalizeWebsiteInput(url);
    if (!canonicalUrl) return toast.error("Enter a website URL");
    setStep("scanning"); setProgress(0);
    const tick = setInterval(() => setProgress((p) => Math.min(p + 1, STAGES.length - 1)), 700);
    try {
      const r = await hqApi.post("/control/scan-website", { url: canonicalUrl });
      clearInterval(tick); setProgress(STAGES.length - 1);
      setResult(r.data);
      const ex = r.data.extraction || {};
      const bestEmail = ex.emails?.[0]?.value || "";
      // Phase 3G.12 — the backend now normalises each candidate to E.164
      // and returns `{value, raw, e164, country_code, national, is_valid}`.
      // Prefer the first candidate the extractor confidently normalised.
      const bestPhoneCand = (ex.phones || []).find((p) => p.is_valid) || (ex.phones || [])[0] || null;
      const bestPhone = bestPhoneCand?.e164 || "";
      setChosenEmail(bestEmail); setChosenPhone(bestPhone);
      // Precedence: the extractor's per-candidate country_code (authoritative,
      // deterministic) > address inference > URL/TLD inference > blank.
      const address = ex.address || "";
      const phoneCountry = bestPhoneCand?.country_code || null;
      const addrCountry = inferCountryFromAddress(address);
      const inferredCode = phoneCountry || addrCountry || "";
      setForm({
        business_name: ex.business_name || r.data.scan?.normalized_domain?.split(".")[0] || "",
        contact_name: "", email: bestEmail, phone: bestPhone,
        address,
        country_code: inferredCode,
        country: "",
        website: r.data.scan?.submitted_url || canonicalUrl,
        status: "prospect",
      });
      if (r.data.duplicate_candidates?.length) {
        setDupChoice(r.data.duplicate_candidates[0].business_id);
      }
      setStep("review");
    } catch (err) {
      clearInterval(tick);
      toast.error(formatApiError(err.response?.data?.detail) || "Scan failed");
      setStep("error");
    }
  };

  const confirm = async () => {
    if (!form.business_name.trim()) return toast.error("Business name required");
    setSubmitting(true);
    try {
      const body = {
        scan_id: result.scan.id,
        business_name: form.business_name,
        contact_name: form.contact_name || undefined,
        email: (chosenEmail || form.email || undefined),
        phone: (chosenPhone || form.phone || undefined),
        address: form.address || undefined,
        country: form.country || undefined,
        country_code: form.country_code || undefined,
        website: form.website,
        status: form.status,
        // Phase 3G.11 — no automatic Website Opportunity from a scan.
        create_opportunity: false,
        update_existing_id: dupChoice !== "new" ? dupChoice : undefined,
      };
      const r = await hqApi.post("/control/clients/from-scan", body);
      toast.success(r.data.created ? "Client created from scan" : "Existing client updated");
      onDone && onDone(r.data);
      closeAll();
      navigate(`/hq/clients/${r.data.business_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
    setSubmitting(false);
  };

  const s = result?.scan;
  const cats = s?.categories || {};
  const ex = result?.extraction || {};
  const dupes = result?.duplicate_candidates || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end lg:items-center lg:justify-center p-0 lg:p-6" data-testid="hq-scan-modal">
      <div className="bg-white w-full lg:w-[95vw] lg:max-w-[900px] lg:rounded-2xl overflow-hidden flex flex-col max-h-full">
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Website scanner</div>
            <h2 className="mt-1 font-black text-xl md:text-2xl">
              {mode === "single" ? "Scan a website & add as a client" : "Bulk prospect scan"}
            </h2>
          </div>
          <button onClick={closeAll} data-testid="hq-scan-close" className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        <div className="flex gap-1 px-5 pt-3 border-b border-[#EAEAEA]" data-testid="hq-scan-tabs">
          {[["single", "Single scan"], ["bulk", "Bulk prospect scan"]].map(([k, label]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`hq-scan-tab-${k}`}
              className={`px-4 py-2 text-[11px] font-black tracking-[0.16em] uppercase border-b-2 ${
                mode === k ? "border-[#FF6A00] text-[#0A0A0A]" : "border-transparent text-[#8A8A8A] hover:text-[#0A0A0A]"
              }`}>{label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {mode === "bulk" && <BulkProspectScanPanel onPromoted={onRefetch || onDone}/>}

          {mode === "single" && step === "url" && (
            <form onSubmit={runScan} data-testid="hq-scan-url-step">
              <label className="block max-w-lg">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Website URL</div>
                <input required type="text" data-testid="hq-scan-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="www.example.co.uk" className="w-full px-3 py-2 rounded-lg border"/>
                <div className="text-[11px] text-[#8A8A8A] mt-2">We'll run an Orange Optimise scan, extract public business details, and check Orange Core for duplicates before creating anything.</div>
              </label>
              <div className="mt-5">
                <button type="submit" data-testid="hq-scan-run" className="px-5 py-2.5 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase">
                  <Globe size={13} className="inline mr-1"/> Scan website
                </button>
              </div>
            </form>
          )}

          {mode === "single" && step === "scanning" && (
            <div data-testid="hq-scan-scanning">
              <h3 className="font-black text-lg mb-3">Working on it…</h3>
              {STAGES.map((s, i) => (
                <StagePulse key={i} stage={s} done={i < progress}/>
              ))}
            </div>
          )}

          {mode === "single" && step === "review" && form && (
            <div data-testid="hq-scan-review" className="space-y-5">
              <div className="p-4 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7] space-y-4">
                <OrangeScoreReveal
                  score={s?.orange_score ?? 0}
                  band={(s?.orange_score ?? 0) >= 80 ? "good" : (s?.orange_score ?? 0) >= 50 ? "needs_attention" : "poor"}
                  subtitle={s?.normalized_domain}
                  revealKey={s?.id || s?.normalized_domain}
                  size="small"
                  showRing={false}
                  testId="hq-scan-score-reveal"
                />
                {Object.keys(cats).length > 0 && (
                  <CategoriesGrid
                    categories={cats}
                    revealKey={s?.id || s?.normalized_domain}
                    columns="grid-cols-2 md:grid-cols-3"
                    testIdPrefix="hq-scan-cat"
                    staggerMs={80}
                    startDelay={300}
                  />
                )}
              </div>

              {dupes.length > 0 && (
                <div className="p-4 rounded-xl border border-[#FCE1C7] bg-[#FFF7EE]" data-testid="hq-scan-dupe-panel">
                  <div className="flex items-center gap-2"><AlertTriangle size={16} className="text-[#FF6A00]"/><strong>Possible existing client</strong></div>
                  <div className="mt-2 space-y-2">
                    {dupes.map((d) => (
                      <label key={d.business_id} className="flex items-center gap-2 text-sm">
                        <input type="radio" data-testid={`hq-scan-dupe-${d.business_id}`} name="dupe" checked={dupChoice === d.business_id} onChange={() => setDupChoice(d.business_id)}/>
                        <span><strong>{d.business_name}</strong> — matched by {d.reason.replace("_", " ")}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" data-testid="hq-scan-dupe-new" name="dupe" checked={dupChoice === "new"} onChange={() => setDupChoice("new")}/>
                      <span>Create a NEW client instead</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Business name</div><input required data-testid="hq-scan-business-name" value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="w-full px-3 py-2 rounded-lg border"/></label>
                <label className="block"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Contact name</div><input data-testid="hq-scan-contact-name" value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className="w-full px-3 py-2 rounded-lg border"/></label>
                <label className="block"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Email</div>
                  {ex.emails?.length > 1 ? (
                    <select data-testid="hq-scan-email-select" value={chosenEmail} onChange={(e) => setChosenEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border">
                      <option value="">— select —</option>
                      {ex.emails.map((em) => <option key={em.value} value={em.value}>{em.value} ({em.source}·{em.confidence})</option>)}
                    </select>
                  ) : <input type="email" data-testid="hq-scan-email" value={chosenEmail} onChange={(e) => setChosenEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>}
                </label>
                {/* Discovered phone dropdown when multiple candidates —
                    values are E.164 (matches backend contract) so
                    PhoneCountryFields receives a clean initial value. */}
                {ex.phones?.length > 1 && (
                  <label className="block">
                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">
                      Discovered phone numbers
                    </div>
                    <select
                      data-testid="hq-scan-phone-select"
                      value={chosenPhone}
                      onChange={(e) => setChosenPhone(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border"
                    >
                      <option value="">— select —</option>
                      {ex.phones.map((p) => {
                        const val = p.e164 || "";
                        const label = p.national || p.raw || p.value;
                        return (
                          <option key={p.raw || p.value} value={val}
                                  disabled={!val}>
                            {label} ({p.source}·{p.confidence}{p.is_valid ? "" : "·unparsed"})
                          </option>
                        );
                      })}
                    </select>
                  </label>
                )}
                <label className="block md:col-span-2"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Address</div><input data-testid="hq-scan-address" value={form.address} onChange={(e) => {
                  const address = e.target.value;
                  const nextCode = form.country_code || inferCountryFromAddress(address) || "";
                  setForm({ ...form, address, country_code: nextCode });
                }} className="w-full px-3 py-2 rounded-lg border"/></label>
                <label className="block md:col-span-2"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Website</div><input data-testid="hq-scan-website" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className="w-full px-3 py-2 rounded-lg border"/></label>
                {/* Phase 3G.12 — shared country + phone-country selector. */}
                <div className="md:col-span-2" data-testid="hq-scan-phonecountry">
                  <PhoneCountryFields
                    value={{
                      phone: chosenPhone || form.phone || "",
                      country: form.country || "",
                      country_code: form.country_code || "",
                    }}
                    onChange={(next) => {
                      setForm({ ...form, country: next.country ?? form.country,
                                country_code: next.country_code ?? form.country_code });
                      if (next.phone !== undefined) setChosenPhone(next.phone);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {mode === "single" && step === "error" && (
            <div className="p-6 text-center text-[#C1350B]">Scan failed. Please try again.</div>
          )}
        </div>

        {step === "review" && form && (
          <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
            <button onClick={closeAll} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
            <button onClick={confirm} disabled={submitting} data-testid="hq-scan-confirm" className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
              {submitting ? "Saving…" : dupChoice !== "new" ? "Update existing client" : "Create client"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
